import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { QuickViewSelector } from '../../components/menus/quick-view-selector'
import { useGetRepository } from '../../utils/api-client/api-hooks.ts'
import { useBranchFilter, useRepositoryDetailsPageParams, useSelectedTool } from './repository-details-page.hooks.tsx'
import { createScanQuickViewSelectItem, SCAN_QUICK_VIEW_IDS, type ScanQuickViewId } from './scan-quick-view-config'
import {
  parseScanView,
  patchSearchParams,
  ScanListLoadingSkeleton,
  SCANS_LIST_PAGE_SIZE,
  ScansList,
  useScanQuickViewCounts,
} from './scans-list.tsx'

export const ScansTab: React.FC = () => {
  return (
    <div>
      <ScanTabHeader />
      <ScansTabList />
    </div>
  )
}

const ScansTabList: React.FC = () => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { selectedTool } = useSelectedTool()
  const { branch, enabled } = useBranchFilter()
  const { data: repository } = useGetRepository({ repositoryId })

  if (!enabled) {
    return <ScanListLoadingSkeleton pageSize={SCANS_LIST_PAGE_SIZE} />
  }

  return (
    <ScansList
      repositoryIds={[repositoryId]}
      tools={selectedTool ? [selectedTool] : []}
      branch={branch}
      repository={repository}
    />
  )
}

const ScanTabHeader: React.FC = () => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { selectedTool } = useSelectedTool()
  const branchFilter = useBranchFilter()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedQuickView = parseScanView(searchParams.get('scan-view'))
  const scanQuickViewCounts = useScanQuickViewCounts({
    repositoryIds: [repositoryId],
    tools: selectedTool ? [selectedTool] : [],
    ...branchFilter,
  })

  const quickViews = useMemo(
    () => SCAN_QUICK_VIEW_IDS.map(id => createScanQuickViewSelectItem(id, scanQuickViewCounts[id])),
    [scanQuickViewCounts]
  )

  const onSelectedQuickViewChange = useCallback(
    (view: ScanQuickViewId) => {
      setSearchParams(prev => patchSearchParams(prev, { 'scan-view': view, 'page-number': null }))
    },
    [setSearchParams]
  )

  return (
    <QuickViewSelector
      quickViews={quickViews}
      selectedQuickView={selectedQuickView}
      onSelectedQuickViewChange={onSelectedQuickViewChange}
    />
  )
}
